import { useState, useEffect } from "react";

import CustomInput from "../../../common/CustomInput";
import ConfirmationModalNew from "../../../common/ConfirmationModalNew";
import { capitalizeFirstLetter } from "../../../../helpers/functions";
import { communicationTypeOptions, reasonOptions, sourceOptions } from "../../create/options";
import { WhenOptions } from "../service-steps/medication/data";

const DiscontinueDialog = ({
  open = false,
  drug = null,
  handleClose = () => { },
  confirmAction = (data) => { },
  onCross = () => { },
}) => {
  const getTodayDateString = () => new Date().toISOString().split("T")[0];
  // Do not inject today's date into dropdown options; use predefined WhenOptions
  const whenOptions = WhenOptions;
  const [inactiveDate, setInactiveDate] = useState(getTodayDateString());
  // keep whenDiscontinued as a token (e.g. 'today', 'Add Specific Date', 'Not Known')
  const [whenDiscontinued, setWhenDiscontinued] = useState("today");
  const [source, setSource] = useState("patient");
  const [communicationType, setCommunicationType] = useState("patient_medication_list");
  const [reason, setReason] = useState("patient_medication_list");
  const [otherReason, setOtherReason] = useState("");

  // Set default when dialog opens
  const reset = () => {
    const today = getTodayDateString();
    setInactiveDate(today);
    setWhenDiscontinued("today");
    setSource("patient");
    setCommunicationType("patient_medication_list");
    setReason("patient_medication_list");
    setOtherReason("");
  };

  // Set default when dialog opens
  useEffect(() => {
    if (open) {
      reset();
    }
  }, [open]);

  const handleDialogClose = () => {
    handleClose();
    reset();
  };

  const handleDialogCross = () => {
    onCross();
    reset();
  };

  return (
    <ConfirmationModalNew
      open={open}
      handleClose={handleDialogClose}
      onCross={handleDialogCross}
      content={() => (
        <div className="flex flex-col gap-8">
          <div className="flex flex-col gap-4">
            <p>{capitalizeFirstLetter(drug?.displayName)}</p>

            {/* When Discontinued Dropdown */}
              <CustomInput
              options={whenOptions}
              name="whenDiscontinued"
              className="col-span-12 w-full !bg-white"
              inputClassName="!min-h-[30px]"
              label="Discontinue Date"
              type="dropdown"
              value={whenDiscontinued}
              selectedDisplayLabel={whenDiscontinued === "today" ? inactiveDate : undefined}
              onChange={(e) => {
                // e.value is a token from WhenOptions (e.g. "Not Known", "Add Specific Date", etc.)
                setWhenDiscontinued(e.value);
                // If selecting a non-date option, clear inactiveDate; if selecting Add Specific Date, leave inactiveDate for user to fill
                if (e.value !== "Add Specific Date" && e.value !== "today") {
                  setInactiveDate("");
                }
              }}
              required={true}
              menuPlacement="bottom"
              customMaxHeight="max-h-[192px]"
            />

            {/* Date Input - Only show when "Add Specific Date" is selected */}
            {whenDiscontinued === "Add Specific Date" && (
              <CustomInput
                name="inactiveDate"
                className="col-span-12 w-full !bg-white"
                inputClassName="!min-h-[30px]"
                label=""
                type="date"
                value={inactiveDate}
                onChange={setInactiveDate}
                required={true}
                clearableDatePicker={true}
              />
            )}

            {/* Reason for Discontinue Dropdown */}
            <CustomInput
              options={reasonOptions}
              name="reason"
              className="col-span-12 w-full !bg-white"
              inputClassName="!min-h-[30px]"
              label="Reason for Discontinue"
              type="dropdown"
              value={reason}
              onChange={(e) => setReason(e.value)}
              required={true}
              menuPlacement="bottom"
              customMaxHeight="max-h-[192px]"
            />

            {/* Other Reason Text Input */}
            {reason === "other" && (
              <CustomInput
                name="otherReason"
                className="col-span-12 w-full !bg-white"
                inputClassName="!min-h-[30px]"
                label="Other Reason"
                placeholder="Please specify other reason"
                type="text"
                value={otherReason}
                onChange={(e) => setOtherReason(e.target.value)}
                required={true}
                />
            )}

            {/* Source Dropdown */}
            <CustomInput
              options={sourceOptions}
              name="source"
              className="col-span-12 w-full !bg-white"
              inputClassName="!min-h-[30px]"
              label="Source"
              type="dropdown"
              value={source}
              onChange={(e) => setSource(e.value)}
              required={true}
             />

            {/* Communication Type Dropdown */}
            <CustomInput
              options={communicationTypeOptions}
              name="communicationType"
              className="col-span-12 w-full !bg-white"
              inputClassName="!min-h-[30px]"
              label="Communication Type"
              type="dropdown"
              value={communicationType}
              onChange={(e) => setCommunicationType(e.value)}
              required={true}
              menuPlacement="bottom"
              customMaxHeight="max-h-[192px]"
            />

          </div>
        </div>
      )}
      className="flex flex-col gap-8 max-w-lg w-[630px] bg-gray-20"
      title="Confirm Discontinue Medication"
      titleClassName="text-teal-300 text-2xl font-semibold"
      headClassName="p-[24px]"
      confirmText="Discontinue"
      confirmAction={() => {
        const discontinueData = {
          inactiveDate: (whenDiscontinued === "Add Specific Date" || whenDiscontinued === "today") ? inactiveDate : null,
          whenDiscontinued,
          source: source, // Single value
          communicationType,
          reason,
          otherReason: reason === "other" ? otherReason : null
        };
        confirmAction(discontinueData);
        reset();
      }}
      cancelText="Cancel"
      cancelAction={handleDialogClose}
      disableConfirm={
        !whenDiscontinued ||
        (whenDiscontinued === "Add Specific Date" && !inactiveDate) ||
        !source ||
        !communicationType ||
        !reason ||
        (reason === "other" && !otherReason)
      }
    />
  );
};

export default DiscontinueDialog;
